import React from "react";
import { LAYOUT_MODES } from "../lib/constants";

const Box = ({ className = "", label = "" }) => (
  <div className={`flex items-center justify-center text-[7px] uppercase tracking-[0.15em] font-bold ${className}`}>
    {label}
  </div>
);

/**
 * Small visual preview of a layout, rendered in a 16:9 box.
 * - mode: any LAYOUT_MODES.* (CUSTOM uses customCfg)
 * - active: whether this miniature is on a selected button (inverts color)
 * - customCfg: when mode === CUSTOM, the customCfg drives the rendering
 */
export const LayoutMiniature = ({ mode, active = false, customCfg = null }) => {
  const fg = active ? "bg-background/30" : "bg-foreground/80";
  const fg2 = active ? "bg-background/15" : "bg-foreground/40";
  const wrap = "w-full aspect-video flex relative overflow-hidden border border-current/20";

  if (mode === LAYOUT_MODES.CUSTOM) {
    return <LayoutPreview customCfg={customCfg} mode={mode} small fg={fg} fg2={fg2} />;
  }

  switch (mode) {
    case LAYOUT_MODES.MEDIA_RIGHT:
      return (
        <div className={wrap}>
          <Box className={`w-2/5 h-full ${fg}`} label="P" />
          <Box className={`w-3/5 h-full ${fg2}`} label="M" />
        </div>
      );
    case LAYOUT_MODES.MEDIA_LEFT:
      return (
        <div className={wrap}>
          <Box className={`w-3/5 h-full ${fg2}`} label="M" />
          <Box className={`w-2/5 h-full ${fg}`} label="P" />
        </div>
      );
    case LAYOUT_MODES.PASSWORD_TOP:
      return (
        <div className={`${wrap} flex-col`}>
          <Box className={`h-1/2 w-full ${fg}`} label="P" />
          <Box className={`h-1/2 w-full ${fg2}`} label="M" />
        </div>
      );
    case LAYOUT_MODES.MEDIA_TOP:
      return (
        <div className={`${wrap} flex-col`}>
          <Box className={`h-1/2 w-full ${fg2}`} label="M" />
          <Box className={`h-1/2 w-full ${fg}`} label="P" />
        </div>
      );
    case LAYOUT_MODES.PASSWORD_70_RIGHT:
      return (
        <div className={wrap}>
          <Box className={`w-[30%] h-full ${fg2}`} label="M" />
          <Box className={`w-[70%] h-full ${fg}`} label="P" />
        </div>
      );
    case LAYOUT_MODES.MEDIA_70_RIGHT:
      return (
        <div className={wrap}>
          <Box className={`w-[30%] h-full ${fg}`} label="P" />
          <Box className={`w-[70%] h-full ${fg2}`} label="M" />
        </div>
      );
    case LAYOUT_MODES.FULLSCREEN_PASSWORD:
      return <div className={wrap}><Box className={`w-full h-full ${fg}`} label="P" /></div>;
    case LAYOUT_MODES.FULLSCREEN_MEDIA:
      return <div className={wrap}><Box className={`w-full h-full ${fg2}`} label="M" /></div>;
    case LAYOUT_MODES.PIP_MEDIA_BR:
      return (
        <div className={wrap}>
          <Box className={`absolute inset-0 ${fg}`} label="P" />
          <Box className={`absolute bottom-1 right-1 w-1/3 h-1/3 ${fg2} border border-current/30`} label="M" />
        </div>
      );
    case LAYOUT_MODES.PIP_PASSWORD_BR:
      return (
        <div className={wrap}>
          <Box className={`absolute inset-0 ${fg2}`} label="M" />
          <Box className={`absolute bottom-1 right-1 w-1/3 h-1/3 ${fg} border border-current/30`} label="P" />
        </div>
      );
    case LAYOUT_MODES.CARDS:
      return (
        <div className={`${wrap} flex-col p-0.5 gap-0.5`}>
          <div className="flex gap-0.5 flex-1 min-h-0">
            <Box className={`flex-[3] ${fg} rounded-sm`} label="P" />
            <Box className={`flex-[2] ${fg2} rounded-sm`} label="M" />
          </div>
          <div className="flex gap-0.5 h-1/3">
            <Box className={`flex-1 ${fg2} rounded-sm border border-current/30`} label="N" />
            <Box className={`flex-1 ${fg2} rounded-sm border border-current/30`} label="N" />
            <Box className={`flex-1 ${fg2} rounded-sm border border-current/30`} label="N" />
            <Box className={`flex-1 ${fg} rounded-sm`} label="⏱" />
          </div>
        </div>
      );
    default:
      return (
        <div className={wrap}>
          <Box className={`w-full h-full ${fg2}`} label="?" />
        </div>
      );
  }
};

/**
 * Larger preview of a custom layout, used in the admin's custom-layout panel.
 */
export const LayoutPreview = ({ customCfg, small = false, fg, fg2 }) => {
  const c = customCfg || {};
  const ratio = Math.min(80, Math.max(20, c.passwordRatio || 50));
  const pos = c.passwordPosition || "left";
  const orientation = c.orientation || "horizontal";
  fg = fg || "bg-foreground/80";
  fg2 = fg2 || "bg-foreground/40";
  const wrap = `w-full ${small ? "aspect-video" : "h-full"} flex relative overflow-hidden border border-foreground/20`;

  if (orientation === "pip") {
    const target = c.pipTarget === "password" ? "password" : "media";
    const pipSize = Math.min(45, Math.max(10, c.pipSize || 25));
    const corner = (pos && pos.length === 2 ? pos : "br");
    const cornerCls = {
      tl: "top-2 left-2",
      tr: "top-2 right-2",
      bl: "bottom-2 left-2",
      br: "bottom-2 right-2",
    }[corner] || "bottom-2 right-2";
    const bigCls = target === "media" ? fg : fg2;
    const smallCls = target === "media" ? fg2 : fg;
    const bigLabel = target === "media" ? "P" : "M";
    const smallLabel = target === "media" ? "M" : "P";
    return (
      <div className={wrap}>
        <Box className={`absolute inset-0 ${bigCls}`} label={bigLabel} />
        <div
          className={`absolute ${cornerCls} ${smallCls} border border-foreground/30`}
          style={{ width: `${pipSize}%`, height: `${pipSize * 0.6}%`, minWidth: 30, minHeight: 18 }}
        >
          <Box className="w-full h-full" label={smallLabel} />
        </div>
      </div>
    );
  }

  if (orientation === "vertical") {
    const passwordOnTop = pos === "top";
    return (
      <div className={`${wrap} flex-col`}>
        <div style={{ height: `${passwordOnTop ? ratio : 100 - ratio}%` }}>
          <Box className={`w-full h-full ${passwordOnTop ? fg : fg2}`} label={passwordOnTop ? "P" : "M"} />
        </div>
        <div style={{ height: `${passwordOnTop ? 100 - ratio : ratio}%` }}>
          <Box className={`w-full h-full ${passwordOnTop ? fg2 : fg}`} label={passwordOnTop ? "M" : "P"} />
        </div>
      </div>
    );
  }

  // horizontal
  const passwordLeft = pos !== "right";
  return (
    <div className={wrap}>
      <div style={{ width: `${passwordLeft ? ratio : 100 - ratio}%` }} className="h-full">
        <Box className={`w-full h-full ${passwordLeft ? fg : fg2}`} label={passwordLeft ? "P" : "M"} />
      </div>
      <div style={{ width: `${passwordLeft ? 100 - ratio : ratio}%` }} className="h-full">
        <Box className={`w-full h-full ${passwordLeft ? fg2 : fg}`} label={passwordLeft ? "M" : "P"} />
      </div>
    </div>
  );
};
